"use client";

import { c as _c } from "react/compiler-runtime";
import { IconEye, IconMessages } from "@tabler/icons-react";
import Link from "next/link";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { getAllCommentCounts } from "@/lib/server/comments";
import { getAllViewCounts } from "@/lib/server/views";
const numberFormatter = new Intl.NumberFormat(process.env.NEXT_PUBLIC_SITE_LOCALE);
type Stats = {
  views: Record<string, number>;
  comments: Record<string, number>;
  loaded: boolean;
};
const StatsContext = createContext<Stats>({
  views: {},
  comments: {},
  loaded: false
});

/**
 * Provider that fetches ALL post stats in a single batch (2 requests total).
 * Wrap this around any component tree that contains PostStats components.
 */
export const PostStatsProvider = t0 => {
  const $ = _c(6);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      views: {},
      comments: {},
      loaded: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [stats, setStats] = useState(t1);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      Promise.all([getAllViewCounts(), getAllCommentCounts()]).then(t4 => {
        const [views, comments] = t4;
        setStats({
          views,
          comments,
          loaded: true
        });
      }).catch(err => {
        console.error("[post-stats] error fetching stats:", err);
        setStats({
          views: {},
          comments: {},
          loaded: true
        });
      });
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== children || $[4] !== stats) {
    t4 = <StatsContext.Provider value={stats}>{children}</StatsContext.Provider>;
    $[3] = children;
    $[4] = stats;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};

/**
 * Displays view/comment badges for a single post.
 * Must be used within a PostStatsProvider.
 */
const PostStats = t0 => {
  const $ = _c(9);
  const {
    slug
  } = t0;
  const {
    views,
    comments,
    loaded
  } = useContext(StatsContext);
  if (!loaded) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <><Skeleton className="inline-block h-5 w-16 translate-y-[-2px] rounded-4xl border border-transparent align-middle" /><Skeleton className="inline-block h-5 w-12 translate-y-[-2px] rounded-4xl border border-transparent align-middle" /></>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const viewCount = views[slug] ?? 0;
  const commentCount = comments[slug] ?? 0;
  let t1;
  if ($[1] !== viewCount) {
    t1 = viewCount > 0 && <Badge variant="secondary" className="gap-[5px] text-[11px] text-foreground/80 tabular-nums"><IconEye className="text-foreground/65" aria-hidden="true" />{numberFormatter.format(viewCount)}</Badge>;
    $[1] = viewCount;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== commentCount || $[4] !== slug) {
    t2 = commentCount > 0 && <Badge variant="secondary" className="gap-[5px] text-[11px] text-foreground/80 tabular-nums" render={<Link href={`/${slug}#comments`} title={`${numberFormatter.format(commentCount)} ${commentCount === 1 ? "comment" : "comments"}`} />}><IconMessages className="text-foreground/65" aria-hidden="true" />{numberFormatter.format(commentCount)}</Badge>;
    $[3] = commentCount;
    $[4] = slug;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
export { PostStats };